import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { encode } from "gpt-tokenizer";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { isCorrectOpenResponse } from "utils/challenge";
import { request } from "hooks/useOkoApi/useOkoApi";
import {
  submitHeadlinesJob,
  pollHeadlinesJob,
  JobExpired,
  AccessRevoked,
  JobFailed,
  PollingTimeout,
  PollingAborted,
} from "components/pages/Educator/Headlines/headlinesJobs";
import {
  DetailsLinkIcon,
  GlowIcon,
  GrowIcon,
  NextStepIcon,
  StatusIcon,
} from "components/pages/Educator/Headlines/headlinesIcons";
import { logError, LogCategory } from "utils/sentryLogger";
import { saveFilters, resolveClassId } from "utils/persistFilters";
import { DateFilterDropdown, SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { useAvailableModels } from "hooks/useAvailableModels";
import { ProficiencyLevelBadge } from "components/pages/Educator/Proficiency/ProficiencyLevelBadge";
import {
  PromptFormula,
  hasFormulaText,
} from "components/pages/Session/Activity/activities/ConsensusActivity/PromptFormula";
import { useSessionData } from "components/pages/Admin/CustomReport/hooks/useSessionData";
import { SessionFetcher } from "components/pages/Admin/CustomReport/SessionFetcher";
import { AnalysisFieldSelector } from "components/pages/Admin/CustomReport/AnalysisFieldSelector";
import { formatTokenCount } from "components/pages/Admin/CustomReport/formatters";

const StandardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#605DEC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke="#605DEC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke="#605DEC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoGlyph = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#002E19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const HeadlinesTooltip = styled(Tooltip)`
  opacity: 1 !important;

  .tooltip-inner {
    background: #eaeafc;
    color: #00140b;
    border-radius: 20px;
    padding: 16px;
    text-align: left;
    max-width: 280px;
    font-family: "Inter", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0;
  }

  &.bs-tooltip-top .tooltip-arrow::before,
  &.bs-tooltip-auto[x-placement^="top"] .tooltip-arrow::before {
    border-top-color: #eaeafc;
  }
  &.bs-tooltip-bottom .tooltip-arrow::before,
  &.bs-tooltip-auto[x-placement^="bottom"] .tooltip-arrow::before {
    border-bottom-color: #eaeafc;
  }
  &.bs-tooltip-left .tooltip-arrow::before,
  &.bs-tooltip-auto[x-placement^="left"] .tooltip-arrow::before {
    border-left-color: #eaeafc;
  }
  &.bs-tooltip-right .tooltip-arrow::before,
  &.bs-tooltip-auto[x-placement^="right"] .tooltip-arrow::before {
    border-right-color: #eaeafc;
  }
`;

const StudentNameList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    margin: 0;
    padding: 0;
  }
`;

const OPTION_LETTERS = ["a", "b", "c", "d", "e", "f"];

const parseCorrectAnswerLabel = (correctAnswer) => {
  if (!correctAnswer) return "";
  const match = correctAnswer.match(/^[A-Z]\s*[:.\-)]\s*(.+)$/i);
  return (match ? match[1] : correctAnswer).trim();
};

const sameAnswer = (a, b) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

const groupOpenResponses = (studentAnswers) => {
  const groups = new Map();
  studentAnswers.forEach((s) => {
    if (!s.answer) return;
    const key = s.answer.trim();
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  });
  return Array.from(groups.entries()).map(([solution, responses]) => ({
    solution,
    responses,
  }));
};

const QuestionsCarousel = ({ questions }) => {
  const [index, setIndex] = useState(0);
  const [showAnswers, setShowAnswers] = useState(false);
  const total = questions.length;
  const safeIndex = Math.min(index, total - 1);
  const current = questions[safeIndex];

  const correctLabel = parseCorrectAnswerLabel(current.correct_answer);
  const studentAnswers = Array.isArray(current.student_answers)
    ? current.student_answers
    : [];

  const questionImage = current.image_url && (
    <QuestionImage src={current.image_url} alt="Question visual" />
  );

  const isMultipleChoice =
    current.question_type === "multiple_choice" &&
    Array.isArray(current.answer_options) &&
    current.answer_options.length > 0;

  return (
    <QuestionsSection>
      <CarouselHeader>
        <CarouselControls>
          <NavButton
            type="button"
            onClick={() => setIndex((i) => Math.max(i - 1, 0))}
            disabled={safeIndex === 0}
            aria-label="Previous question"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </NavButton>
          <CarouselLabel>
            <strong>Question</strong>
            <span>
              {safeIndex + 1} of {total}
            </span>
          </CarouselLabel>
          <NavButton
            type="button"
            onClick={() => setIndex((i) => Math.min(i + 1, total - 1))}
            disabled={safeIndex === total - 1}
            aria-label="Next question"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </NavButton>
        </CarouselControls>
        <ShowAnswersToggle>
          <input
            type="checkbox"
            id={`headlines-show-answers-${safeIndex}`}
            checked={showAnswers}
            onChange={(e) => setShowAnswers(e.target.checked)}
          />
          <label htmlFor={`headlines-show-answers-${safeIndex}`}>
            Show answers
          </label>
        </ShowAnswersToggle>
      </CarouselHeader>

      <QuestionBody>
        <QuestionText>{current.question_text}</QuestionText>

        {hasFormulaText(current.prompt_image_latex) ? (
          <PromptFormula latex={current.prompt_image_latex} fallback={questionImage} />
        ) : (
          questionImage
        )}

        {isMultipleChoice ? (
          <OptionsList>
            {current.answer_options.map((opt, i) => {
              const isCorrect = sameAnswer(opt, correctLabel);
              const selectionsForOption = studentAnswers.filter((s) =>
                sameAnswer(s.answer, opt)
              );
              let pillState = "default";
              if (showAnswers) pillState = isCorrect ? "correct" : "incorrect";
              const showTooltip =
                showAnswers && selectionsForOption.length > 0;
              const pill = (
                <OptionPill $state={pillState}>
                  <OptionLetter>{OPTION_LETTERS[i] || i + 1} )</OptionLetter>
                  <OptionText>{opt}</OptionText>
                </OptionPill>
              );
              return (
                <OptionRowWrap key={`${opt}-${i}`}>
                  {showTooltip ? (
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <HeadlinesTooltip id={`option-tooltip-${i}`}>
                          <StudentNameList>
                            {selectionsForOption.map((s, si) => (
                              <li key={`${s.student_name || "anon"}-${si}`}>
                                {s.student_name || "Unknown student"}
                              </li>
                            ))}
                          </StudentNameList>
                        </HeadlinesTooltip>
                      }
                    >
                      {pill}
                    </OverlayTrigger>
                  ) : (
                    pill
                  )}
                  {showAnswers && selectionsForOption.length > 0 && (
                    <ResponseDotsContainer>
                      {selectionsForOption.map((s, di) => (
                        <ResponseDot
                          key={`${s.student_name || "anon"}-${di}`}
                          $isCorrect={isCorrect}
                        />
                      ))}
                    </ResponseDotsContainer>
                  )}
                </OptionRowWrap>
              );
            })}
          </OptionsList>
        ) : (
          showAnswers && (
            <OpenResponseGroups>
              {(() => {
                const groups = groupOpenResponses(studentAnswers);
                const hasCorrectInResponses =
                  current.correct_answer &&
                  groups.some(({ solution }) =>
                    isCorrectOpenResponse(solution, correctLabel)
                  );
                if (groups.length === 0 && !current.correct_answer) {
                  return <EmptyResponses>No responses recorded</EmptyResponses>;
                }
                return (
                  <>
                    {!hasCorrectInResponses && current.correct_answer && (
                      <OpenResponseRow>
                        <OpenResponseInput $isCorrect>
                          {current.correct_answer}
                        </OpenResponseInput>
                      </OpenResponseRow>
                    )}
                    {groups.map(({ solution, responses }) => {
                      const isCorrect = isCorrectOpenResponse(solution, correctLabel);
                      const pill = (
                        <OpenResponseInput $isCorrect={isCorrect}>
                          {solution}
                        </OpenResponseInput>
                      );
                      return (
                        <OpenResponseRow key={solution}>
                          {responses.length > 0 ? (
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <HeadlinesTooltip
                                  id={`open-response-tooltip-${solution}`}
                                >
                                  <StudentNameList>
                                    {responses.map((s, si) => (
                                      <li
                                        key={`${s.student_name || "anon"}-${si}`}
                                      >
                                        {s.student_name || "Unknown student"}
                                      </li>
                                    ))}
                                  </StudentNameList>
                                </HeadlinesTooltip>
                              }
                            >
                              {pill}
                            </OverlayTrigger>
                          ) : (
                            pill
                          )}
                          <ResponseDotsContainer>
                            {responses.map((s, di) => (
                              <ResponseDot
                                key={`${s.student_name || "anon"}-${di}`}
                                $isCorrect={isCorrect}
                              />
                            ))}
                          </ResponseDotsContainer>
                        </OpenResponseRow>
                      );
                    })}
                  </>
                );
              })()}
            </OpenResponseGroups>
          )
        )}
      </QuestionBody>
    </QuestionsSection>
  );
};

const LEVEL_COLORS = {
  1: colors.pink500,
  2: colors.orange500,
  3: colors.green500,
};

const nextDayIso = (iso) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

export const Headlines = ({
  educatorId = null,
  teacherEmail = null,
  sharedClassId,
  onClassChange,
  onGoToProficiency,
  adminUserData,
}) => {
  const isAdmin = Boolean(adminUserData?.can_access_user_admin);

  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassIdInternal] = useState(null);
  const setSelectedClassId = (id) => {
    setSelectedClassIdInternal(id);
    if (onClassChange) onClassChange(id);
  };
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedDateMode, setSelectedDateMode] = useState("weekly");

  const [headlines, setHeadlines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);

  const [tuningOpen, setTuningOpen] = useState(false);
  const { provider, setProvider, modelName, setModelName, modelOptions, modelsLoading } =
    useAvailableModels();
  const [temperature, setTemperature] = useState(0.3);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [debugMode, setDebugMode] = useState(false);
  const [thinkingLevel, setThinkingLevel] = useState("");
  const [sessionsPanelOpen, setSessionsPanelOpen] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const sessionData = useSessionData(teacherEmail, selectedClassId);
  const {
    analysisData,
    availableFields,
    selectedFields,
    selectedSessions,
    selectedSessionIds,
    estimatedTokenCount,
    setStartDate: setSessionsStart,
    setEndDate: setSessionsEnd,
  } = sessionData;

  const trimmedAnalysisData = useMemo(() => {
    if (!isAdmin || !analysisData || analysisData.length === 0) return null;
    const includedFields = availableFields.filter((f) => selectedFields[f]);
    if (includedFields.length === 0) return null;
    const filtered = analysisData
      .filter((item) => selectedSessions[item.session_id])
      .map((item) => {
        if (!item.analysis || typeof item.analysis !== "object") return null;
        const trimmed = {};
        includedFields.forEach((f) => {
          if (f in item.analysis) trimmed[f] = item.analysis[f];
        });
        return { session_id: item.session_id, analysis: trimmed };
      })
      .filter(Boolean);
    return filtered.length > 0 ? filtered : null;
  }, [isAdmin, analysisData, availableFields, selectedFields, selectedSessions]);

  const cacheRef = useRef(new Map());
  const abortRef = useRef(null);
  const selectedClass = classes.find((c) => c.id === selectedClassId);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (selectedClassId) {
      saveFilters("headlines", {
        selectedClassId,
        selectedWeek,
        selectedEndDate,
        selectedDateMode,
      });
    }
  }, [selectedClassId, selectedWeek, selectedEndDate, selectedDateMode]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        let classList;
        if (educatorId) {
          const data = await request(`class-report/educator/${educatorId}/classes`, {
            method: "GET",
          });
          classList = Array.isArray(data) ? data : data?.classes || [];
        } else {
          const educatorData = await request("account/educator", { method: "GET" });
          classList = educatorData?.classes || [];
        }
        setClasses(classList);
        if (classList.length > 0) {
          if (sharedClassId && classList.some((c) => c.id === sharedClassId)) {
            setSelectedClassId(sharedClassId);
          } else {
            const { classId, saved } = await resolveClassId("headlines", classList);
            if (saved?.selectedWeek) setSelectedWeek(saved.selectedWeek);
            if (saved?.selectedEndDate) setSelectedEndDate(saved.selectedEndDate);
            if (saved?.selectedDateMode) setSelectedDateMode(saved.selectedDateMode);
            setSelectedClassId(classId);
          }
        }
      } catch (e) {
        logError("Failed to fetch headlines classes", e, { category: LogCategory.ERROR });
        setError(e?.detail || e?.message || "An unknown error occurred.");
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educatorId]);

  const handleDatesLoaded = useCallback((weeks, days) => {
    setSelectedWeek((prev) => {
      if (prev) return prev;
      if (weeks.length > 0) {
        setSelectedDateMode("weekly");
        return weeks[0].week_of;
      }
      if (days.length > 0) {
        setSelectedDateMode("daily");
        return days[0].date;
      }
      return null;
    });
  }, []);

  useEffect(() => {
    if (!isAdmin || !selectedWeek) return;
    let endDate;
    if (selectedDateMode === "daily") {
      endDate = selectedWeek;
    } else if (selectedDateMode === "custom" && selectedEndDate) {
      endDate = selectedEndDate;
    } else {
      const d = new Date(selectedWeek + "T00:00:00");
      d.setDate(d.getDate() + 6);
      endDate = d.toISOString().split("T")[0];
    }
    setSessionsStart(selectedWeek);
    setSessionsEnd(endDate);
  }, [isAdmin, selectedWeek, selectedEndDate, selectedDateMode, setSessionsStart, setSessionsEnd]);

  const analysisSignature = useMemo(() => {
    if (!trimmedAnalysisData) return "";
    const includedFields = availableFields.filter((f) => selectedFields[f]).sort();
    const sessionIds = trimmedAnalysisData.map((i) => i.session_id).sort();
    return `${sessionIds.join(",")}::${includedFields.join(",")}`;
  }, [trimmedAnalysisData, availableFields, selectedFields]);

  const cacheKey = useMemo(() => {
    if (!selectedClassId || !selectedWeek) return null;
    return [
      selectedClassId,
      selectedDateMode,
      selectedWeek,
      selectedEndDate || "",
      isAdmin ? provider || "" : "",
      isAdmin ? modelName || "" : "",
      isAdmin ? String(temperature) : "",
      isAdmin ? systemPrompt : "",
      isAdmin ? userPrompt : "",
      isAdmin ? analysisSignature : "",
      isAdmin && debugMode ? "debug" : "",
      isAdmin ? thinkingLevel : "",
    ].join("|");
  }, [
    selectedClassId,
    selectedWeek,
    selectedEndDate,
    selectedDateMode,
    isAdmin,
    provider,
    modelName,
    temperature,
    systemPrompt,
    userPrompt,
    thinkingLevel,
    analysisSignature,
    debugMode,
  ]);

  useEffect(() => {
    if (!cacheKey) return;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setHeadlines(cached);
      setError(null);
    }
  }, [cacheKey]);

  const handleGenerate = useCallback(async () => {
    if (!selectedClassId || !selectedWeek || !cacheKey) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setReportLoading(true);
    setHeadlines(null);
    setError(null);
    setHasGenerated(true);
    try {
      const payload = {};
      if (educatorId) payload.educator_id = educatorId;
      if (selectedDateMode === "daily") {
        payload.start_date = selectedWeek;
        payload.end_date = nextDayIso(selectedWeek);
      } else if (selectedDateMode === "custom" && selectedEndDate) {
        payload.start_date = selectedWeek;
        payload.end_date = selectedEndDate;
      } else {
        payload.week_of = selectedWeek;
      }
      if (isAdmin) {
        if (provider) payload.provider = provider;
        if (modelName) payload.model_name = modelName;
        if (temperature != null) payload.temperature = temperature;
        if (systemPrompt.trim()) payload.system_prompt = systemPrompt;
        if (userPrompt.trim()) payload.prompt = userPrompt;
        if (trimmedAnalysisData) payload.analysis_data = trimmedAnalysisData;
        if (debugMode) payload.debug = true;
        if (thinkingLevel) payload.thinking_level = thinkingLevel;
      }
      const { job_id } = await submitHeadlinesJob(
        selectedClassId,
        "proficiency",
        payload,
        controller.signal
      );
      const result = await pollHeadlinesJob(job_id, controller.signal);
      if (controller.signal.aborted) return;
      cacheRef.current.set(cacheKey, result);
      setHeadlines(result);
    } catch (e) {
      if (e instanceof PollingAborted || controller.signal.aborted) return;
      if (e instanceof JobExpired || e instanceof PollingTimeout) {
        setError(e.message);
      } else if (e instanceof AccessRevoked) {
        setError(e.message);
      } else if (e instanceof JobFailed) {
        setError(e.message);
      } else if (e?.status === 404) {
        setHeadlines(null);
      } else if (e?.status === 403) {
        setError("You don't have access to this classroom.");
      } else if (e?.status === 502) {
        setError("LLM provider temporarily unavailable. Please retry.");
      } else if (e?.status === 422) {
        setError(e?.detail || e?.message || "Invalid request.");
      } else {
        logError("Failed to fetch headlines", e, { category: LogCategory.ERROR });
        setError(e?.detail || e?.message || "Failed to load headlines.");
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      if (!controller.signal.aborted) setReportLoading(false);
    }
  }, [
    cacheKey,
    selectedClassId,
    selectedWeek,
    selectedEndDate,
    selectedDateMode,
    educatorId,
    isAdmin,
    provider,
    modelName,
    temperature,
    systemPrompt,
    userPrompt,
    trimmedAnalysisData,
    debugMode,
    thinkingLevel,
  ]);

  const canGenerate = Boolean(selectedClassId && selectedWeek && !reportLoading);
  const cachedForCurrent = cacheKey ? cacheRef.current.has(cacheKey) : false;
  const isStale = hasGenerated && !cachedForCurrent && !reportLoading;

  const promptTokenCount = useMemo(() => {
    if (!isAdmin) return 0;
    const combined = `${systemPrompt || ""}\n${userPrompt || ""}`.trim();
    return combined ? encode(combined).length : 0;
  }, [isAdmin, systemPrompt, userPrompt]);
  const contextTokenCount = trimmedAnalysisData ? estimatedTokenCount : 0;
  const sessionsInContext = trimmedAnalysisData ? trimmedAnalysisData.length : 0;

  if (loading) {
    return (
      <Container>
        <CardShell>
          <HeadlinesSkeleton />
        </CardShell>
      </Container>
    );
  }

  return (
    <Container>
      <FilterBar>
        <FilterGroup>
          <FilterLabel>Class</FilterLabel>
          <SelectDropdown
            ariaLabel="Class"
            value={selectedClassId}
            displayText={classDisplayName(selectedClass) || "Select a class"}
            options={classes.map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
            groupBySchoolYear
            onChange={setSelectedClassId}
          />
        </FilterGroup>

        <DateFilterGroup>
          <DateFilterDropdown
            label="Date"
            classroomId={selectedClassId}
            educatorId={educatorId}
            startDate={selectedWeek}
            endDate={selectedEndDate}
            selectedDateMode={selectedDateMode}
            onDatesLoaded={handleDatesLoaded}
            maxRangeDays={14}
            onDateChange={(start, end, mode) => {
              setSelectedWeek(start);
              setSelectedEndDate(end);
              setSelectedDateMode(mode);
            }}
          />
          <DateRangeNote>Custom range limited to 2 weeks · applies to this tab only</DateRangeNote>
        </DateFilterGroup>
      </FilterBar>

      {isAdmin && teacherEmail && (
        <SessionsSection>
          <SessionsHeader onClick={() => setSessionsPanelOpen((v) => !v)}>
            <span>
              Admin: LLM context sessions
              {trimmedAnalysisData && (
                <TuningHint>
                  {" "}· {trimmedAnalysisData.length} session{trimmedAnalysisData.length === 1 ? "" : "s"} ·{" "}
                  {formatTokenCount(estimatedTokenCount)} tokens
                </TuningHint>
              )}
              {!trimmedAnalysisData && (
                <TuningHint> (omit to use server-side DB fetch)</TuningHint>
              )}
            </span>
            <FontAwesomeIcon
              icon={faChevronDown}
              style={{
                transform: sessionsPanelOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          </SessionsHeader>
          {sessionsPanelOpen && (
            <SessionsBody>
              <SessionFetcher
                {...sessionData}
                hideDateInputs
                onFetchSessions={sessionData.handleFetchSessions}
                onSelectAll={sessionData.handleSelectAll}
                onToggleSession={sessionData.handleToggleSession}
              />
              {analysisData && availableFields.length > 0 && (
                <AnalysisFieldSelector
                  availableFields={availableFields}
                  selectedFields={selectedFields}
                  allFieldsSelected={sessionData.allFieldsSelected}
                  estimatedTokenCount={estimatedTokenCount}
                  selectedSessionCount={selectedSessionIds.length}
                  onToggleField={sessionData.handleToggleField}
                  onSelectAllFields={sessionData.handleSelectAllFields}
                  previewData={trimmedAnalysisData || []}
                />
              )}
            </SessionsBody>
          )}
        </SessionsSection>
      )}

      {isAdmin && (
        <TuningPanel>
          <TuningHeader onClick={() => setTuningOpen((v) => !v)}>
            <span>Admin: LLM tuning</span>
            <FontAwesomeIcon
              icon={faChevronDown}
              style={{
                transform: tuningOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          </TuningHeader>
          {tuningOpen && (
            <TuningBody>
              <TuningField>
                <FilterLabel>Provider</FilterLabel>
                <TuningSelect
                  value={provider || ""}
                  onChange={(e) => setProvider(e.target.value)}
                  disabled={modelsLoading}
                >
                  {modelOptions &&
                    Object.keys(modelOptions).map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                </TuningSelect>
              </TuningField>
              <TuningField>
                <FilterLabel>Model</FilterLabel>
                <TuningSelect
                  value={modelName || ""}
                  onChange={(e) => setModelName(e.target.value)}
                  disabled={modelsLoading || !modelOptions?.[provider]}
                >
                  {modelOptions?.[provider]?.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </TuningSelect>
              </TuningField>
              <TuningField>
                <FilterLabel>Temperature: {temperature.toFixed(2)}</FilterLabel>
                <TuningRange
                  type="range"
                  min="0"
                  max="2"
                  step="0.05"
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                />
              </TuningField>
              <TuningField>
                <FilterLabel>
                  Thinking Level <TuningHint>(Gemini 3.x only)</TuningHint>
                </FilterLabel>
                <TuningSelect
                  value={thinkingLevel}
                  onChange={(e) => setThinkingLevel(e.target.value)}
                >
                  <option value="">Default</option>
                  <option value="minimal">Minimal</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </TuningSelect>
              </TuningField>
              <TuningFieldFull>
                <FilterLabel>
                  System Prompt <TuningHint>(leave blank for Langfuse default)</TuningHint>
                </FilterLabel>
                <TuningTextarea
                  rows={6}
                  placeholder="You are a math instructional coach..."
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                />
              </TuningFieldFull>
              <TuningFieldFull>
                <FilterLabel>
                  Prompt <TuningHint>(leave blank for default user prompt)</TuningHint>
                </FilterLabel>
                <TuningTextarea
                  rows={4}
                  placeholder="Focus on common misconceptions about area decomposition."
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                />
              </TuningFieldFull>
              <TuningFieldFull>
                <DebugToggleRow>
                  <input
                    type="checkbox"
                    id="headlines-debug-toggle"
                    checked={debugMode}
                    onChange={(e) => setDebugMode(e.target.checked)}
                  />
                  <label htmlFor="headlines-debug-toggle">
                    <strong>Debug mode</strong>
                    <TuningHint>
                      {" "}— skip the LLM, return the assembled prompt (no model spend, no latency).
                    </TuningHint>
                  </label>
                </DebugToggleRow>
              </TuningFieldFull>
              <TuningActions>
                <TuningSecondaryBtn
                  type="button"
                  disabled={!systemPrompt && !userPrompt}
                  onClick={() => {
                    setSystemPrompt("");
                    setUserPrompt("");
                  }}
                >
                  Reset prompts
                </TuningSecondaryBtn>
              </TuningActions>
            </TuningBody>
          )}
        </TuningPanel>
      )}

      <GenerateBar>
        {isAdmin && (
          <TokenSummary>
            <TokenRow>
              <span>Analysis data</span>
              <TokenValue>{formatTokenCount(contextTokenCount)} tokens</TokenValue>
            </TokenRow>
            <TokenRow>
              <span>Prompt</span>
              <TokenValue>{formatTokenCount(promptTokenCount)} tokens</TokenValue>
            </TokenRow>
            <TokenTotalRow>
              <TotalLabel>Estimated total</TotalLabel>
              <TotalLabel>~{formatTokenCount(contextTokenCount + promptTokenCount)} tokens</TotalLabel>
            </TokenTotalRow>
            <TokenDisclaimer>
              Token counts are approximate and may vary by model and provider.
            </TokenDisclaimer>
          </TokenSummary>
        )}
        <GenerateButton
          type="button"
          disabled={!canGenerate}
          onClick={handleGenerate}
        >
          {reportLoading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "8px" }} />
              Generating headlines...
            </>
          ) : debugMode ? (
            `Generate headlines (debug${sessionsInContext ? ` · ${sessionsInContext} session${sessionsInContext === 1 ? "" : "s"}` : ""})`
          ) : (
            `Generate headlines${sessionsInContext ? ` (${sessionsInContext} session${sessionsInContext === 1 ? "" : "s"})` : ""}`
          )}
        </GenerateButton>
        {isStale && <StaleNote>Inputs have changed — click Generate to refresh.</StaleNote>}
      </GenerateBar>

      <CardShell>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {!error && reportLoading && <HeadlinesSkeleton />}
        {!error && !reportLoading && !headlines && hasGenerated && (
          <EmptyState>No proficiency data for this range.</EmptyState>
        )}
        {!error && !reportLoading && !headlines && !hasGenerated && (
          <EmptyState>
            {selectedWeek
              ? "Click Generate headlines to run the LLM call."
              : "Select a class and date range to begin."}
          </EmptyState>
        )}
        {!error && !reportLoading && headlines && (
          <HeadlinesCard data={headlines} onGoToProficiency={onGoToProficiency} />
        )}
      </CardShell>

      {isAdmin && !reportLoading && headlines?.llm_input_debug && (
        <DebugPanel>
          <DebugPanelTitle>Debug: assembled LLM input</DebugPanelTitle>
          <DebugBlock>
            <DebugBlockLabel>System prompt</DebugBlockLabel>
            <DebugPre>{headlines.llm_input_debug.system_prompt || "(empty)"}</DebugPre>
          </DebugBlock>
          <DebugBlock>
            <DebugBlockLabel>Human prompt</DebugBlockLabel>
            <DebugPre>{headlines.llm_input_debug.human_prompt || "(empty)"}</DebugPre>
          </DebugBlock>
          <DebugBlock>
            <DebugBlockLabel>Context JSON</DebugBlockLabel>
            <DebugPre>{headlines.llm_input_debug.context_json || "(empty)"}</DebugPre>
          </DebugBlock>
        </DebugPanel>
      )}
    </Container>
  );
};

const HeadlinesCard = ({ data, onGoToProficiency }) => {
  const focus = data.focus || {};
  const proficiencyStatus = Array.isArray(data.proficiency_status)
    ? data.proficiency_status
    : [];
  const analyzedQuestions = Array.isArray(data.analyzed_questions)
    ? data.analyzed_questions
    : [];
  const glow = typeof data.glow === "string" ? data.glow : null;
  const grow = typeof data.grow === "string" ? data.grow : null;
  const nextStep = typeof data.next_step === "string" ? data.next_step : "";
  const isDebug = data.llm_input_debug != null;

  const description = focus.full_description || focus.standard_name || focus.standard_code || "";

  return (
    <>
      <Section>
        <SectionHeader>
          <StandardIcon />
          <SectionTitle>Top Standard</SectionTitle>
          <OverlayTrigger
            placement="top"
            overlay={
              <HeadlinesTooltip id="top-standard-info-tooltip">
                The standard that received the highest total amount of student practice during this time period.
              </HeadlinesTooltip>
            }
          >
            <InfoIcon>
              <InfoGlyph />
            </InfoIcon>
          </OverlayTrigger>
        </SectionHeader>
        <SectionBodyIndented>
          <StandardLine>
            {focus.standard_code && <strong>{focus.standard_code} </strong>}
            {focus.standard_code && description && "- "}
            {description}
          </StandardLine>
        </SectionBodyIndented>
        {analyzedQuestions.length > 0 && (
          <QuestionsCarousel questions={analyzedQuestions} />
        )}
      </Section>

      <Divider />

      <Section>
        <SectionHeader>
          <StatusIcon />
          <SectionTitle>Proficiency Status</SectionTitle>
        </SectionHeader>
        <ProficiencyRow>
          {proficiencyStatus.map((lvl) => (
            <ProficiencyItem key={lvl.level}>
              <ProficiencyLevelBadge
                level={lvl.level}
                color={LEVEL_COLORS[lvl.level] || colors.pink500}
                size={48}
                fontSize={18}
              />
              <ProficiencyText>
                <ProficiencyCount>
                  {lvl.student_count} {lvl.student_count === 1 ? "Student" : "Students"}
                </ProficiencyCount>
                <ProficiencyName>{lvl.name}</ProficiencyName>
              </ProficiencyText>
            </ProficiencyItem>
          ))}
        </ProficiencyRow>
      </Section>

      {!isDebug && (
        <>
          {(glow || grow) && (
            <TwoColumn>
              <NarrativeBlock icon={<GlowIcon />} title="Glow" text={glow} />
              <NarrativeBlock icon={<GrowIcon />} title="Grow" text={grow} />
            </TwoColumn>
          )}
          <NarrativeBlock
            icon={<NextStepIcon />}
            title="Next Step"
            text={nextStep}
            asParagraph
          />
        </>
      )}

      <LinkButton as="a" href="#section-proficiency">
        <span>Go to Math Proficiency Details section</span>
        <DetailsLinkIcon />
      </LinkButton>
    </>
  );
};

// Strips LLM annotation tags like [CLIMATE:ANTI-COLLABORATIVE] from narrative
// text. Tag format: [UPPERCASE_LABEL:UPPERCASE_VALUE] (value may contain
// dashes/underscores). Also collapses leftover whitespace.
const stripBracketTags = (text) => {
  if (!text) return text;
  return text
    .replace(/\s*\[[A-Z][A-Z0-9_]*:[A-Z0-9_-]+\]\s*/g, " ")
    .replace(/ {2,}/g, " ")
    .replace(/ ([,.;:!?])/g, "$1")
    .trim();
};

const NarrativeBlock = ({ icon, title, text, asParagraph = false }) => {
  if (!text) return null;
  const cleaned = stripBracketTags(text).replace(/\*\*/g, "").trim();
  if (!cleaned) return null;
  const sentences = cleaned
    .split(/(?<=\.)\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const asList = !asParagraph && sentences.length > 1;
  return (
    <Section>
      <SectionHeader>
        {icon}
        <SectionTitle>{title}</SectionTitle>
      </SectionHeader>
      {asList ? (
        <NarrativeList>
          {sentences.map((s, i) => (
            <NarrativeListItem key={i}>{s}</NarrativeListItem>
          ))}
        </NarrativeList>
      ) : (
        <NarrativeBody>{cleaned}</NarrativeBody>
      )}
    </Section>
  );
};

const HeadlinesSkeleton = () => (
  <>
    <Section>
      <SkeletonBar $w="160px" $h="22px" />
      <SkeletonBar $w="90%" $h="14px" style={{ marginTop: 12 }} />
      <SkeletonBar $w="75%" $h="14px" style={{ marginTop: 6 }} />
    </Section>
    <Divider />
    <Section>
      <SkeletonBar $w="180px" $h="22px" />
      <ProficiencyRow>
        {[1, 2, 3].map((i) => (
          <ProficiencyItem key={i}>
            <SkeletonBar $w="80px" $h="80px" style={{ borderRadius: 12 }} />
            <ProficiencyText>
              <SkeletonBar $w="110px" $h="14px" />
              <SkeletonBar $w="80px" $h="14px" style={{ marginTop: 6 }} />
            </ProficiencyText>
          </ProficiencyItem>
        ))}
      </ProficiencyRow>
    </Section>
    <TwoColumn>
      {[0, 1].map((col) => (
        <Section key={col}>
          <SkeletonBar $w="80px" $h="22px" />
          <SkeletonBar $w="95%" $h="14px" style={{ marginTop: 12 }} />
          <SkeletonBar $w="85%" $h="14px" style={{ marginTop: 6 }} />
          <SkeletonBar $w="70%" $h="14px" style={{ marginTop: 6 }} />
        </Section>
      ))}
    </TwoColumn>
    <Section>
      <SkeletonBar $w="100px" $h="22px" />
      <SkeletonBar $w="90%" $h="14px" style={{ marginTop: 12 }} />
    </Section>
  </>
);

const skeletonPulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 0.3; }
  100% { opacity: 0.6; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 32px;
  background-color: ${colors.milkGlass200};
  min-height: calc(100vh - 140px);
`;

const FilterBar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 0 16px;
`;

const DateFilterGroup = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 300px;
`;

const DateRangeNote = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 11px;
  color: ${colors.milkGlass950};
  margin-top: 4px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 300px;
`;

const FilterLabel = styled.label`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: black;
`;

const TuningPanel = styled.div`
  background: white;
  border: 1px solid ${colors.blue300};
  border-radius: 8px;
  margin: 0 16px;
`;

const TuningHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  padding: 12px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
`;

const TuningBody = styled.div`
  display: flex;
  gap: 16px;
  padding: 0 16px 16px;
  flex-wrap: wrap;
`;

const TuningField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 200px;
  flex: 1;
`;

const TuningSelect = styled.select`
  border: 1px solid ${colors.blue300};
  border-radius: 6px;
  padding: 8px 12px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods500};
  background: white;
`;

const TuningRange = styled.input`
  width: 100%;
`;

const TuningFieldFull = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 100%;
  min-width: 100%;
`;

const TuningHint = styled.span`
  color: ${colors.milkGlass950};
  font-weight: 400;
  font-size: 12px;
`;

const TuningTextarea = styled.textarea`
  border: 1px solid ${colors.blue300};
  border-radius: 6px;
  padding: 8px 12px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: ${colors.nightlyWoods500};
  background: white;
  resize: vertical;
`;

const TuningActions = styled.div`
  display: flex;
  gap: 8px;
  flex: 1 1 100%;
  justify-content: flex-end;
`;

const GenerateBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0 16px;
`;

const GenerateButton = styled(Button)`
  background-color: ${colors.nightlyWoods500};
  border: none;
  border-radius: 6px;
  font-size: 16px;
  height: 40px;
  padding: 0 16px;
  width: 100%;

  &:hover,
  &:focus,
  &:active {
    background-color: ${colors.nightlyWoods700};
  }
`;

const TokenSummary = styled.div`
  padding: 12px 16px;
  background-color: ${colors.milkGlass200};
  border-radius: 8px;
  font-size: 13px;
  color: #6c757d;
`;

const TokenRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const TokenValue = styled.span`
  color: ${colors.nightlyWoods900};
  font-weight: 500;
`;

const TokenTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid ${colors.milkGlass600};
  padding-top: 6px;
  margin-top: 2px;
`;

const TotalLabel = styled.span`
  font-weight: 600;
  color: ${colors.nightlyWoods900};
`;

const TokenDisclaimer = styled.div`
  margin-top: 6px;
  font-size: 11px;
  font-style: italic;
`;

const StaleNote = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: ${colors.orange700};
`;

const DebugToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods500};

  label {
    cursor: pointer;
    margin: 0;
  }

  strong {
    font-weight: 600;
  }
`;

const SessionsSection = styled.div`
  background: white;
  border: 1px solid ${colors.blue300};
  border-radius: 8px;
  margin: 0 16px;
`;

const SessionsHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  padding: 12px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  text-align: left;
`;

const SessionsBody = styled.div`
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DebugPanel = styled.div`
  margin: 0 16px;
  background: white;
  border: 1px solid ${colors.blue300};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DebugPanelTitle = styled.h3`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${colors.nightlyWoods500};
`;

const DebugBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DebugBlockLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.milkGlass950};
`;

const DebugPre = styled.pre`
  margin: 0;
  padding: 12px;
  background: ${colors.milkGlass500};
  border: 1px solid ${colors.milkGlass600};
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  color: ${colors.nightlyWoods500};
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 360px;
  overflow: auto;
`;

const TuningSecondaryBtn = styled.button`
  background: white;
  color: ${colors.blue500};
  border: 1px solid ${colors.blue300};
  border-radius: 6px;
  padding: 8px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CardShell = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0px 1px 3px rgba(14, 26, 49, 0.1), 0px 1px 2px rgba(14, 26, 49, 0.06);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 0 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionTitle = styled.h3`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const SectionBodyIndented = styled.div`
  padding: 0 32px;
`;

const InfoIcon = styled.span`
  color: ${colors.milkGlass950};
  cursor: help;
  font-size: 16px;
`;

const StandardLine = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
  margin: 0;

  strong {
    font-weight: 700;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${colors.blue300};
  margin: 0;
`;

const ProficiencyRow = styled.div`
  display: flex;
  gap: 24px;
  padding: 16px 40px;
  align-items: center;
  justify-content: space-around;
  flex-wrap: wrap;
`;

const ProficiencyItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 200px;
  justify-content: center;
`;

const ProficiencyText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: ${colors.nightlyWoods500};
  min-width: 120px;
`;

const ProficiencyName = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
`;

const ProficiencyCount = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
`;

const TwoColumn = styled.div`
  display: flex;
  gap: 16px;

  > * {
    flex: 1 1 0;
    min-width: 0;
  }
`;

const NarrativeBody = styled.p`
  margin: 0;
  padding: 0 24px 0 48px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
`;

const NarrativeList = styled.ul`
  margin: 0;
  padding: 0 24px 0 68px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
  list-style-type: disc;
  list-style-position: outside;
`;

const NarrativeListItem = styled.li`
  display: list-item;
  margin-bottom: 4px;
`;

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 48px;
  padding: 0 16px;
  background: none;
  border: none;
  border-radius: 6px;
  color: ${colors.blue500};
  font-family: "Inter", sans-serif;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    background: ${colors.blue50};
  }
`;

const ErrorBanner = styled.div`
  padding: 16px;
  background: ${colors.orange50};
  color: ${colors.orange700};
  border-radius: 6px;
  border: 1px solid ${colors.orange300};
  font-family: "Inter", sans-serif;
  font-size: 14px;
`;

const EmptyState = styled.div`
  padding: 64px 16px;
  text-align: center;
  color: ${colors.milkGlass950};
  font-family: "Inter", sans-serif;
  font-size: 16px;
`;

const SkeletonBar = styled.div`
  width: ${(p) => p.$w || "60px"};
  height: ${(p) => p.$h || "14px"};
  border-radius: 4px;
  background: ${colors.milkGlass600};
  animation: ${skeletonPulse} 1.5s ease-in-out infinite;
`;

const QuestionsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 24px 0 8px;
  width: 100%;
`;

const CarouselHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 16px;
`;

const CarouselControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: none;
  padding: 0;
  background: ${colors.nightlyWoods500};
  color: white;
  cursor: pointer;
  font-size: 12px;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    background: #D5D3CE;
    cursor: not-allowed;
  }
`;

const CarouselLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #858B87;
  letter-spacing: 0;

  strong {
    font-weight: 700;
  }
`;

const ShowAnswersToggle = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: ${colors.nightlyWoods500};

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    accent-color: ${colors.green500};
    cursor: pointer;
    margin: 0;
  }

  label {
    margin: 0;
    cursor: pointer;
  }
`;

const QuestionBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 0 16px;
`;

const QuestionText = styled.p`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: ${colors.nightlyWoods500};
  text-align: center;
`;

const QuestionImage = styled.img`
  max-width: 100%;
  max-height: 280px;
  border-radius: 6px;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  width: 100%;
`;

const OptionRowWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const optionPillBg = (state) => {
  if (state === "correct") return colors.green500;
  return "white";
};

const optionPillBorder = (state) => {
  if (state === "correct") return colors.green500;
  if (state === "incorrect") return colors.pink500;
  return colors.blue500;
};

const optionPillColor = (state) => {
  if (state === "correct") return "white";
  return "#65558F";
};

const OptionPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 240px;
  padding: 8px 24px;
  border-radius: 100px;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 24px;
  background: ${(p) => optionPillBg(p.$state)};
  border: 1px solid ${(p) => optionPillBorder(p.$state)};
  color: ${(p) => optionPillColor(p.$state)};
`;

const OptionLetter = styled.span`
  font-size: 14px;
`;

const OptionText = styled.span`
  font-weight: 500;
`;

const ResponseDotsContainer = styled.div`
  display: flex;
  gap: 4px;
  position: absolute;
  left: 100%;
  padding-left: 8px;
`;

const ResponseDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${(p) => (p.$isCorrect ? colors.green500 : colors.pink500)};
`;

const OpenResponseGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  margin-top: 8px;
  width: 100%;
`;

const OpenResponseRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const OpenResponseInput = styled.div`
  min-width: 150px;
  height: 50px;
  padding: 0 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  background-color: ${(p) => (p.$isCorrect ? colors.green500 : "white")};
  border: 1px solid ${(p) => (p.$isCorrect ? colors.green500 : colors.pink500)};
  color: ${(p) => (p.$isCorrect ? "white" : colors.nightlyWoods500)};
`;

const EmptyResponses = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: italic;
  color: ${colors.milkGlass950};
`;
